import React from 'react';
import { Attraction } from '../../types';
import { useLocale } from '../../context/LocaleContext';
import { t } from '../../utils/i18n';

const isHttpUrl = (value?: string): boolean =>
  Boolean(value && /^https?:\/\//i.test(value.trim()));

interface AttractionDirectoryTableProps {
  attractions: Attraction[];
}

const AttractionDirectoryTable: React.FC<AttractionDirectoryTableProps> = ({
  attractions,
}) => {
  const { locale } = useLocale();
  const isEn = locale === 'en';

  const pickText = (zh?: string, en?: string) => (isEn ? en || zh || '' : zh || en || '');

  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--dir-border)] bg-white">
      <table className="w-full text-sm text-left border-collapse">
        <thead>
          <tr className="border-b border-[var(--dir-border)] text-[var(--dir-muted)]">
            <th className="py-3 px-4 font-medium w-[26%] sm:w-[22%]">
              {isEn ? 'Attraction' : '景点'}
            </th>
            <th className="py-3 px-4 font-medium">{isEn ? 'About & tickets' : '简介与门票'}</th>
            <th className="py-3 px-4 font-medium text-right w-[28%] sm:w-[24%]">
              {isEn ? 'Official site' : '官网'}
            </th>
          </tr>
        </thead>
        <tbody>
          {attractions.map((att) => {
            const nameZh = (att.attractionsNameZh || att.attractionsName || '').trim();
            const nameEn = (att.attractionsNameEn || att.attractionsName || '').trim();
            const about = pickText(att.description, att.descriptionEn);
            const link = att.officialLink;
            const hasLink = isHttpUrl(link);
            const displayNameEn = nameEn || nameZh;

            return (
              <tr
                key={`${att.id}-${nameZh}-${nameEn}`}
                className="dir-row dir-row-hover align-top"
              >
                <td className="py-3 px-4">
                  {isEn ? (
                    displayNameEn ? (
                      <div className="font-medium text-[var(--dir-text)] leading-snug">
                        {displayNameEn}
                      </div>
                    ) : (
                      <span className="text-[var(--dir-muted)]">—</span>
                    )
                  ) : (
                    <>
                      {nameZh ? (
                        <div className="font-medium text-[var(--dir-text)] leading-snug">{nameZh}</div>
                      ) : null}
                      {nameEn && nameEn !== nameZh ? (
                        <div className="text-[var(--dir-muted)] mt-0.5 leading-snug">{nameEn}</div>
                      ) : null}
                      {!nameZh && !nameEn ? (
                        <span className="text-[var(--dir-muted)]">—</span>
                      ) : null}
                    </>
                  )}
                </td>
                <td className="py-3 px-4 text-[var(--dir-muted)] leading-relaxed">
                  {about ? <p className="mb-2">{about}</p> : null}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    {att.priceRange ? (
                      <span>
                        {t(locale, 'price')}: {att.priceRange}
                        {att.currency ? ` ${att.currency}` : ''}
                      </span>
                    ) : null}
                    {att.bookingRequired ? (
                      <span>{t(locale, 'bookInAdvance')}</span>
                    ) : null}
                    {att.advanceBookingDays ? (
                      <span>
                        {t(locale, 'daysInAdvance', {
                          n: String(att.advanceBookingDays),
                        })}
                      </span>
                    ) : null}
                    {!about && !att.priceRange && !att.bookingRequired && !att.advanceBookingDays ? (
                      <span>—</span>
                    ) : null}
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  {hasLink ? (
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dir-link-coral inline-flex items-center gap-1 font-medium whitespace-nowrap"
                    >
                      {isEn ? 'Official website' : '官方网站'}
                      <span aria-hidden>↗</span>
                    </a>
                  ) : link ? (
                    <span className="text-xs text-[var(--dir-muted)] break-all">{link}</span>
                  ) : (
                    <span className="text-xs text-[var(--dir-muted)]">
                      {isEn ? 'No link' : '暂无链接'}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AttractionDirectoryTable;
